import React, { useRef } from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useLocation } from 'react-router-dom';
import HourlyCell from './HourlyCell';
import { formatDateJapanese } from '../../utils/todayUtils';
import { detectLanguageFromPathname } from '../../i18n';

import PropTypes from 'prop-types';

const HourlyDetails = ({ todayData, handleScroll }) => {
    const location = useLocation();
    const isEnglish = detectLanguageFromPathname(location.pathname) === 'en';
    const isMobile = useMediaQuery('(max-width:768px)');
    const isSmallMobile = useMediaQuery('(max-width:480px)');
    const todayScrollRef = useRef(null);
    const yesterdayScrollRef = useRef(null);
    const lastYearScrollRef = useRef(null);
    
    if (!todayData?.data) {
        return null;
    }
    
    const todayPredictionEntry = todayData.data.today_prediction_hourly || null;
    const yesterdayEntry = todayData.data.yesterday_hourly || null;
    const lastYearEntry = todayData.data.last_year_today_hourly;
    const visibleStartHour = 7;
    const visibleEndHour = 22;
    const filterVisibleHours = (hourly = []) =>
        hourly.filter((hourData) => hourData.hour >= visibleStartHour && hourData.hour <= visibleEndHour);
    const todayPredictionHours = filterVisibleHours(todayPredictionEntry?.hourly_congestion);
    const yesterdayHours = filterVisibleHours(yesterdayEntry?.hourly_congestion);
    const lastYearHours = filterVisibleHours(lastYearEntry?.hourly_congestion);
    
    if (!todayPredictionEntry?.data_available && !yesterdayEntry?.data_available && !lastYearEntry?.data_available) {
        return (
            <Box sx={{ 
                textAlign: 'center', 
                py: 4,
                backgroundColor: '#f9f9f9',
                borderRadius: '8px',
                border: '1px solid #ddd'
            }}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                    {isEnglish ? 'Hourly detail data is not available' : '時間別の詳細データは利用できません'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {isEnglish ? 'Please refer to past data (weekly trend above)' : '過去データ（上記の週間動向）をご参照ください'}
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            {/* 今日の予測データ */}
            {todayPredictionEntry?.data_available && todayPredictionHours.length > 0 && (
                <Box sx={{ mb: 0.5 }}>
                    <Typography 
                        variant="body1"
                        sx={{ 
                            fontWeight: 'bold', 
                            mb: 0.25,
                            color: 'text.primary',
                            fontSize: isMobile ? '0.9rem' : '1rem'
                        }}
                    >
                        {isEnglish ? 'Today (Forecast)' : '今日（予測）'} - {formatDateJapanese(todayPredictionEntry.date, isEnglish ? 'en' : 'ja')}
                    </Typography>
                    
                    <Box sx={{ 
                        border: '1px solid #ddd', 
                        borderRadius: '8px', 
                        overflow: 'hidden',
                        width: '100%',
                        maxWidth: '100%',
                        height: 'fit-content',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <Box 
                            sx={{ 
                                overflowX: 'auto',
                                WebkitOverflowScrolling: 'touch',
                                height: 'auto',
                                flex: 'none',
                                '&::-webkit-scrollbar': {
                                    height: '8px',
                                    width: '8px'
                                },
                                '&::-webkit-scrollbar-track': {
                                    backgroundColor: '#f1f1f1',
                                    borderRadius: '4px'
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: '#c1c1c1',
                                    borderRadius: '4px'
                                }
                            }}
                            ref={todayScrollRef}
                            onScroll={() => handleScroll(todayScrollRef, [yesterdayScrollRef, lastYearScrollRef])}
                        >
                            {/* 時間帯ヘッダー */}
                            <Box sx={{ 
                                display: 'flex',
                                backgroundColor: '#f5f5f5',
                                borderBottom: '1px solid #ddd',
                                width: 'fit-content',
                                minWidth: '100%',
                                flex: 'none'
                            }}>
                                {todayPredictionHours.map((hourData, index) => (
                                    <Box 
                                        key={`hour-header-${hourData.hour}`} 
                                        sx={{ 
                                            minWidth: isMobile ? (isSmallMobile ? '32px' : '42px') : '42px',
                                            width: isMobile ? (isSmallMobile ? '32px' : '42px') : '42px',
                                            textAlign: 'center', 
                                            padding: isMobile ? '4px 2px' : '6px 2px',
                                            borderRight: index !== todayPredictionHours.length - 1 ? '1px solid #ddd' : 'none',
                                            flexShrink: 0,
                                            height: isMobile ? (isSmallMobile ? '34px' : '38px') : '38px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <Typography 
                                            variant={isSmallMobile ? "bodyS" : "bodyM"}
                                            fontWeight="bold"
                                            sx={{
                                                fontSize: isMobile ? (isSmallMobile ? '12px' : '14px') : '16px'
                                            }}
                                        >
                                            {isEnglish ? `${hourData.hour}` : `${hourData.hour}時`}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                            
                            {/* データセル */}
                            <Box sx={{ 
                                display: 'flex',
                                width: 'fit-content',
                                minWidth: '100%',
                                flex: 'none',
                                height: 'auto'
                            }}>
                                {todayPredictionHours.map((hourData, index) => (
                                    <HourlyCell 
                                        key={hourData.hour}
                                        hourData={hourData} 
                                        index={index} 
                                        totalLength={todayPredictionHours.length} 
                                        isGrayCell={hourData.congestion === 0}
                                        showPredictionBadge={hourData.source === 'prediction'}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}

            {/* 昨日のデータ */}
            {yesterdayEntry?.data_available && yesterdayHours.length > 0 && (
                <Box sx={{ mb: 0.5 }}>
                    <Typography 
                        variant="body1"
                        sx={{ 
                            fontWeight: 'bold', 
                            mb: 0.25,
                            color: 'text.primary',
                            fontSize: isMobile ? '0.9rem' : '1rem'
                        }}
                    >
                        {isEnglish ? 'Yesterday' : '昨日'} - {formatDateJapanese(yesterdayEntry.date, isEnglish ? 'en' : 'ja')}
                    </Typography>
                    
                    <Box sx={{ 
                        border: '1px solid #ddd', 
                        borderRadius: '8px', 
                        overflow: 'hidden',
                        width: '100%',
                        maxWidth: '100%',
                        height: 'fit-content',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <Box 
                            sx={{ 
                                overflowX: 'auto',
                                WebkitOverflowScrolling: 'touch',
                                height: 'auto',
                                flex: 'none',
                                '&::-webkit-scrollbar': {
                                    height: '8px',
                                    width: '8px'
                                },
                                '&::-webkit-scrollbar-track': {
                                    backgroundColor: '#f1f1f1',
                                    borderRadius: '4px'
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: '#c1c1c1',
                                    borderRadius: '4px'
                                }
                            }}
                            ref={yesterdayScrollRef}
                            onScroll={() => handleScroll(yesterdayScrollRef, [todayScrollRef, lastYearScrollRef])}
                        >
                            {/* 時間帯ヘッダー */}
                            <Box sx={{ 
                                display: 'flex',
                                backgroundColor: '#f5f5f5',
                                borderBottom: '1px solid #ddd',
                                width: 'fit-content',
                                minWidth: '100%',
                                flex: 'none'
                            }}>
                                {yesterdayHours.map((hourData, index) => (
                                    <Box 
                                        key={`hour-header-${hourData.hour}`} 
                                        sx={{ 
                                            minWidth: isMobile ? (isSmallMobile ? '32px' : '42px') : '42px',
                                            width: isMobile ? (isSmallMobile ? '32px' : '42px') : '42px',
                                            textAlign: 'center', 
                                            padding: isMobile ? '4px 2px' : '6px 2px',
                                            borderRight: index !== yesterdayHours.length - 1 ? '1px solid #ddd' : 'none',
                                            flexShrink: 0,
                                            height: isMobile ? (isSmallMobile ? '34px' : '38px') : '38px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <Typography 
                                            variant={isSmallMobile ? "bodyS" : "bodyM"}
                                            fontWeight="bold"
                                            sx={{
                                                fontSize: isMobile ? (isSmallMobile ? '12px' : '14px') : '16px'
                                            }}
                                        >
                                            {isEnglish ? `${hourData.hour}` : `${hourData.hour}時`}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                            
                            {/* データセル */}
                            <Box sx={{ 
                                display: 'flex',
                                width: 'fit-content',
                                minWidth: '100%',
                                flex: 'none',
                                height: 'auto'
                            }}>
                                {yesterdayHours.map((hourData, index) => (
                                    <HourlyCell 
                                        key={hourData.hour}
                                        hourData={hourData} 
                                        index={index} 
                                        totalLength={yesterdayHours.length} 
                                        isGrayCell={hourData.congestion === 0}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}
            
            {/* 去年同日のデータ */}
            {lastYearEntry?.data_available && lastYearHours.length > 0 && (
                <Box sx={{ mb: 0.25 }}>
                    <Typography 
                        variant="body1"
                        sx={{ 
                            fontWeight: 'bold', 
                            mb: 0.25,
                            color: 'text.primary',
                            fontSize: isMobile ? '0.9rem' : '1rem'
                        }}
                    >
                        {isEnglish ? 'Same day last year' : '去年の同じ日'} - {formatDateJapanese(lastYearEntry.date, isEnglish ? 'en' : 'ja')}
                    </Typography>
                    
                    <Box sx={{ 
                        border: '1px solid #ddd', 
                        borderRadius: '8px', 
                        overflow: 'hidden',
                        width: '100%',
                        maxWidth: '100%',
                        height: 'fit-content',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <Box 
                            sx={{ 
                                overflowX: 'auto',
                                WebkitOverflowScrolling: 'touch',
                                height: 'auto',
                                flex: 'none',
                                '&::-webkit-scrollbar': {
                                    height: '8px',
                                    width: '8px'
                                },
                                '&::-webkit-scrollbar-track': {
                                    backgroundColor: '#f1f1f1',
                                    borderRadius: '4px'
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: '#c1c1c1',
                                    borderRadius: '4px'
                                }
                            }}
                            ref={lastYearScrollRef}
                            onScroll={() => handleScroll(lastYearScrollRef, [todayScrollRef, yesterdayScrollRef])}
                        >
                            {/* 時間帯ヘッダー */}
                            <Box sx={{ 
                                display: 'flex',
                                backgroundColor: '#f5f5f5',
                                borderBottom: '1px solid #ddd',
                                width: 'fit-content',
                                minWidth: '100%',
                                flex: 'none'
                            }}>
                                {lastYearHours.map((hourData, index) => (
                                    <Box 
                                        key={`hour-header-${hourData.hour}`} 
                                        sx={{ 
                                            minWidth: isMobile ? (isSmallMobile ? '32px' : '42px') : '42px',
                                            width: isMobile ? (isSmallMobile ? '32px' : '42px') : '42px',
                                            textAlign: 'center', 
                                            padding: isMobile ? '4px 2px' : '6px 2px',
                                            borderRight: index !== lastYearHours.length - 1 ? '1px solid #ddd' : 'none',
                                            flexShrink: 0,
                                            height: isMobile ? (isSmallMobile ? '34px' : '38px') : '38px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <Typography 
                                            variant={isSmallMobile ? "bodyS" : "bodyM"}
                                            fontWeight="bold"
                                            sx={{
                                                fontSize: isMobile ? (isSmallMobile ? '12px' : '14px') : '16px'
                                            }}
                                        >
                                            {isEnglish ? `${hourData.hour}` : `${hourData.hour}時`}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                            
                            {/* データセル */}
                            <Box sx={{ 
                                display: 'flex',
                                width: 'fit-content',
                                minWidth: '100%',
                                flex: 'none',
                                height: 'auto'
                            }}>
                                {lastYearHours.map((hourData, index) => (
                                    <HourlyCell 
                                        key={hourData.hour}
                                        hourData={hourData} 
                                        index={index} 
                                        totalLength={lastYearHours.length} 
                                        isGrayCell={hourData.congestion === 0}

                                    />
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

HourlyDetails.propTypes = {
    todayData: PropTypes.shape({
        data: PropTypes.shape({
            today_prediction_hourly: PropTypes.shape({
                data_available: PropTypes.bool,
                date: PropTypes.string,
                hourly_congestion: PropTypes.arrayOf(PropTypes.object)
            }),
            yesterday_hourly: PropTypes.shape({
                data_available: PropTypes.bool,
                date: PropTypes.string,
                hourly_congestion: PropTypes.arrayOf(PropTypes.object)
            }),
            last_year_today_hourly: PropTypes.shape({
                data_available: PropTypes.bool,
                date: PropTypes.string,
                hourly_congestion: PropTypes.arrayOf(PropTypes.object)
            })
        })
    }),
    handleScroll: PropTypes.func.isRequired
};

export default HourlyDetails;
